import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ShieldAlert, Minus, Plus, Search, Users, BarChart3, Building2, Flag, Ban, EyeOff, Eye, ExternalLink, Phone, Mail, MessageCircle, MapPin, Tag, Coins, Package, Image as ImageIcon, Trash2, Play, Newspaper, Pencil, X, Sprout, Upload } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, mediaUrl, formatApiErrorDetail } from "@/lib/api";
import { CategoryMappingReport } from "@/components/CategoryMappingReport";

const PLAN_LABEL = { basic: "Basic", annual: "Annual" };
const NEWS_CATEGORIES = ["GST & Compliance", "Industry News", "Growth Tips", "Announcements", "Market Trends"];
const TABS = [["overview", "Overview", BarChart3], ["subscribers", "Subscribers", Users], ["businesses", "Businesses", Building2], ["seed", "Bulk Seed", Sprout], ["news", "News", Newspaper], ["media", "Media", ImageIcon], ["reports", "Reports", Flag]];

export default function Admin() {
  const [tab, setTab] = useState("overview");
  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-10">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-9 w-9 rounded-lg bg-brand flex items-center justify-center shrink-0">
            <div className="h-4 w-4 rounded-full border-2 border-white" />
          </div>
          <h1 className="font-display font-black tracking-tight text-2xl sm:text-3xl lg:text-4xl text-slate-900">
            IndiaBusiness<span className="text-brand">Circle</span> Admin Console
          </h1>
        </div>
        <div className="flex gap-2 mb-8 overflow-x-auto pb-1">
          {TABS.map(([v, label, Icon]) => (
            <button key={v} data-testid={`admin-tab-${v}`} onClick={() => setTab(v)} className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${tab === v ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>
        {tab === "overview" && <Overview />}
        {tab === "subscribers" && <Subscribers />}
        {tab === "businesses" && <Businesses />}
        {tab === "seed" && <SeedBusinesses />}
        {tab === "news" && <AdminNews />}
        {tab === "media" && <AdminMedia />}
        {tab === "reports" && <Reports />}
      </div>
    </Layout>
  );
}

const STAT_META = [
  ["total_users", "Total Users"], ["incomplete_registrations", "Incomplete Registrations"],
  ["total_businesses", "Total Businesses"], ["gst_verified_businesses", "GST Verified"],
  ["published_businesses", "Published"], ["active_businesses", "Active (30d)"],
  ["total_follows", "Total Follows"], ["total_connections", "Connections"],
  ["total_posts", "Posts"], ["suspended_businesses", "Suspended"], ["open_reports", "Open Reports"],
];
function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)).catch((e) => console.error("Failed to load stats", e)); }, []);
  if (!stats) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-brand" size={28} /></div>;
  return (
    <div data-testid="admin-stats" className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {STAT_META.map(([k, label]) => (
        <div key={k} data-testid={`stat-${k}`} className="rounded-2xl border border-slate-200 bg-white p-5">
          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
          <p className="mt-1 font-display font-black text-3xl text-slate-900">{stats[k]}</p>
        </div>
      ))}
    </div>
  );
}

function Subscribers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const load = async () => { setLoading(true); try { const { data } = await api.get("/admin/subscribers"); setUsers(data); } catch (e) { toast.error("Could not load"); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);
  const adjust = async (id, body) => { try { const { data } = await api.post(`/admin/users/${id}/credits`, body); setUsers((us) => us.map((u) => (u.user_id === id ? { ...u, credits: data.credits } : u))); } catch (e) { toast.error("Failed"); } };
  const OUTREACH_MSG = (u) => `Hi ${u.name || "there"}, I noticed you created an account on IBC but haven't claimed your business yet...`;
  const waHref = (u) => { const d = (u.phone || "").replace(/\D/g, ""); const num = d.length === 10 ? `91${d}` : d; return `https://wa.me/${num}?text=${encodeURIComponent(OUTREACH_MSG(u))}`; };
  const mailHref = (u) => `mailto:${u.email}?subject=${encodeURIComponent("Claim your business on IndiaBusinessCircle")}&body=${encodeURIComponent(OUTREACH_MSG(u))}`;
  const filtered = users.filter((u) => !q || u.email.toLowerCase().includes(q.toLowerCase()) || (u.business_name || "").toLowerCase().includes(q.toLowerCase()) || (u.name || "").toLowerCase().includes(q.toLowerCase()));
  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-brand" size={28} /></div>;
  return (
    <div>
      <div className="relative max-w-md mb-6"><Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
        <Input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users…" className="pl-10 h-11 bg-white" /></div>
      <div className="overflow-x-auto border border-slate-200 rounded-xl bg-white">
        <table className="w-full text-sm" data-testid="admin-subscribers-table">
          <thead><tr className="border-b border-slate-200 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
            <th className="px-4 py-3">User</th><th className="px-4 py-3">Business</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Credits</th><th className="px-4 py-3">Outreach</th><th className="px-4 py-3 text-right">Adjust</th></tr></thead>
          <tbody>{filtered.map((u) => (
            <tr key={u.user_id} data-testid={`admin-row-${u.user_id}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
              <td className="px-4 py-3"><p className="font-semibold text-slate-800">{u.name || "—"}</p><p className="text-xs text-slate-400">{u.email}{u.role === "admin" && <span className="ml-1 text-brand font-bold">· admin</span>}</p></td>
              <td className="px-4 py-3">{u.business_name ? <span className="inline-flex items-center gap-1.5">{u.gst_verified && <ShieldCheck size={14} className="text-[#22C55E]" />}<span className="text-slate-700">{u.business_name}</span></span> : <span data-testid={`admin-incomplete-${u.user_id}`} className="inline-flex rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[11px] font-bold text-amber-700">Not claimed</span>}</td>
              <td className="px-4 py-3">{u.phone ? <span className="text-slate-600 font-medium">{u.phone}</span> : <span className="text-slate-300">—</span>}</td>
              <td className="px-4 py-3">{u.plan_status === "active" ? <span className="inline-flex rounded-full bg-brand-light px-2.5 py-0.5 text-xs font-bold text-brand">{PLAN_LABEL[u.plan] || u.plan}</span> : <span className="text-slate-300 text-xs">No plan</span>}</td>
              <td className="px-4 py-3"><input data-testid={`admin-credits-input-${u.user_id}`} type="number" defaultValue={u.credits} key={`${u.user_id}-${u.credits}`} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} onBlur={(e) => { const n = parseInt(e.target.value, 10); if (!Number.isNaN(n) && n !== u.credits) adjust(u.user_id, { set: n }); }} className="w-20 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 font-semibold text-slate-800 focus:border-brand focus:outline-none" /></td>
              <td className="px-4 py-3"><div className="flex items-center gap-1.5">
                <a data-testid={`admin-email-${u.user_id}`} href={mailHref(u)} title={`Email ${u.email}`} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:border-brand hover:text-brand transition-colors"><Mail size={14} /> Email</a>
                {u.phone ? (
                  <a data-testid={`admin-whatsapp-${u.user_id}`} href={waHref(u)} target="_blank" rel="noopener noreferrer" title="Message on WhatsApp" className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 transition-colors"><MessageCircle size={14} /> WhatsApp</a>
                ) : (
                  <span className="inline-flex items-center gap-1.5 rounded-md border border-slate-100 px-2.5 py-1.5 text-xs font-semibold text-slate-300"><MessageCircle size={14} /> WhatsApp</span>
                )}
              </div></td>
              <td className="px-4 py-3"><div className="flex items-center justify-end gap-1.5">
                <Button data-testid={`admin-minus-${u.user_id}`} onClick={() => adjust(u.user_id, { delta: -1 })} size="icon" variant="outline" className="h-8 w-8 border-slate-200"><Minus size={14} /></Button>
                <Button data-testid={`admin-plus-${u.user_id}`} onClick={() => adjust(u.user_id, { delta: 1 })} size="icon" variant="outline" className="h-8 w-8 border-slate-200"><Plus size={14} /></Button></div></td>
            </tr>))}</tbody>
        </table>
      </div>
    </div>
  );
}

function Businesses() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const load = async (qq) => { setLoading(true); try { const { data } = await api.get("/admin/businesses", { params: { q: qq || undefined, limit: 50 } }); setItems(data.items); } catch (e) { console.error("Failed to load businesses", e); } finally { setLoading(false); } };
  useEffect(() => { const t = setTimeout(() => load(q), 300); return () => clearTimeout(t); }, [q]);
  const suspend = async (id, val) => { try { await api.post(`/admin/businesses/${id}/suspend`, { suspended: val }); setItems((it) => it.map((b) => (b.id === id ? { ...b, suspended: val } : b))); toast.success(val ? "Suspended" : "Reinstated"); } catch (e) { toast.error("Failed"); } };
  const [detailId, setDetailId] = useState(null);
  const [deduping, setDeduping] = useState(false);
  const dedupe = async () => {
    if (!window.confirm("Remove duplicate unclaimed businesses sharing the same GSTIN? The oldest record in each group is kept.")) return;
    setDeduping(true);
    try {
      const { data } = await api.post("/admin/businesses/dedupe");
      toast.success(`Successfully removed ${data.removed} duplicate records. Your directory is now clean.`);
      load(q);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Cleanup failed"); }
    finally { setDeduping(false); }
  };
  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-md"><Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <Input data-testid="admin-businesses-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search businesses…" className="pl-10 h-11 bg-white" /></div>
        <Button data-testid="dedupe-btn" onClick={dedupe} disabled={deduping} variant="outline" className="gap-1.5 font-semibold border-red-300 text-red-600 hover:bg-red-50 shrink-0">
          {deduping ? <Loader2 className="animate-spin" size={16} /> : <Trash2 size={16} />} Remove Duplicates
        </Button>
      </div>
      {loading ? <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-brand" size={28} /></div> : (
        <div className="space-y-3" data-testid="admin-businesses-list">{items.map((b) => (
          <div key={b.id} data-testid={`admin-biz-${b.id}`} className="flex items-center justify-between gap-3 border border-slate-200 rounded-xl bg-white p-4">
            <div className="min-w-0"><p className="font-semibold text-slate-800 flex items-center gap-1.5">{b.business_name}{b.gst_verified && <ShieldCheck size={14} className="text-[#22C55E]" />}{b.suspended && <span className="rounded-full bg-red-50 text-red-600 text-[11px] font-bold px-2 py-0.5">Suspended</span>}</p>
              <p className="text-xs text-slate-400">{b.category} · {[b.city, b.state].filter(Boolean).join(", ")}</p></div>
            <div className="flex items-center gap-2 shrink-0">
              <Button data-testid={`view-full-${b.id}`} onClick={() => setDetailId(b.id)} variant="outline" className="gap-1.5 font-semibold border-brand text-brand hover:bg-brand hover:text-white"><ExternalLink size={15} /> View Full Profile</Button>
              <Button data-testid={`suspend-${b.id}`} onClick={() => suspend(b.id, !b.suspended)} variant="outline" className={`gap-1.5 font-semibold ${b.suspended ? "border-emerald-300 text-emerald-700" : "border-red-300 text-red-600"}`}><Ban size={15} /> {b.suspended ? "Reinstate" : "Suspend"}</Button>
            </div>
          </div>))}</div>
      )}
      <AdminBusinessModal businessId={detailId} onClose={() => setDetailId(null)} onSuspendChange={(id, val) => setItems((it) => it.map((b) => (b.id === id ? { ...b, suspended: val } : b)))} />
    </div>
  );
}

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
    <Icon size={16} className="text-brand mt-0.5 shrink-0" />
    <div className="min-w-0"><p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
      <p className="text-sm font-semibold text-slate-800 break-words">{value || "—"}</p></div>
  </div>
);

function AdminBusinessModal({ businessId, onClose, onSuspendChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!businessId) { setData(null); return; }
    setLoading(true);
    api.get(`/admin/businesses/${businessId}/full`).then(({ data }) => setData(data)).catch((e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load"); }).finally(() => setLoading(false));
  }, [businessId]);
  const b = data?.business; const o = data?.owner; const s = data?.stats;
  const toggleSuspend = async () => { try { const val = !b.suspended; await api.post(`/admin/businesses/${b.id}/suspend`, { suspended: val }); setData((d) => ({ ...d, business: { ...d.business, suspended: val } })); onSuspendChange(b.id, val); toast.success(val ? "Suspended" : "Reinstated"); } catch (e) { toast.error("Failed"); } };
  return (
    <Dialog open={!!businessId} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-business-modal">
        <DialogHeader><DialogTitle className="font-display text-xl font-black tracking-tight">Full Business Profile</DialogTitle><DialogDescription>Admin-only view including contact and GST details hidden on the public site.</DialogDescription></DialogHeader>
        {loading || !b ? <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-brand" size={28} /></div> : (
          <div className="space-y-5">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-display font-bold text-lg text-slate-900" data-testid="admin-modal-business-name">{b.business_name}</h3>
              {b.gst_verified ? <span className="inline-flex items-center gap-1 rounded-full bg-[#DCFCE7] border border-[#BBF7D0] px-2 py-0.5 text-xs font-bold text-[#166534]"><ShieldCheck size={12} className="text-[#22C55E]" /> GST Verified</span>
                : <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-xs font-bold text-amber-700"><ShieldAlert size={12} /> Unverified</span>}
              {b.suspended && <span className="rounded-full bg-red-50 text-red-600 text-[11px] font-bold px-2 py-0.5">Suspended</span>}
            </div>

            <div><p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Contact & GST (admin-only)</p>
              <div className="grid sm:grid-cols-2 gap-2.5">
                <Row icon={Mail} label="Owner Email" value={o?.email} />
                <Row icon={Phone} label="Phone" value={b.phone} />
                <Row icon={ShieldCheck} label="GSTIN (full)" value={b.gst_number} />
                <Row icon={Building2} label="Business Type" value={b.business_type} />
              </div>
            </div>

            <div><p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Profile</p>
              <div className="grid sm:grid-cols-2 gap-2.5">
                <Row icon={Tag} label="Category" value={[b.category, b.subcategory].filter(Boolean).join(" · ")} />
                <Row icon={Building2} label="Trade Name" value={b.trade_name} />
                <Row icon={MapPin} label="Location" value={[b.city, b.state].filter(Boolean).join(", ")} />
                <Row icon={Coins} label="Owner Credits / Plan" value={`${o?.credits ?? 0} · ${o?.plan_status === "active" ? (o.plan || "") : "No plan"}`} />
              </div>
            </div>

            {b.description && <div><p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1">Description</p><p className="text-sm text-slate-700">{b.description}</p></div>}
            {b.products?.length > 0 && <div><p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Products & Services</p><div className="flex flex-wrap gap-1.5">{b.products.map((p, i) => <span key={`${p}-${i}`} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700"><Package size={12} className="text-brand" /> {p}</span>)}</div></div>}
            {b.intents?.length > 0 && <div><p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Looking For</p><div className="flex flex-wrap gap-1.5">{b.intents.map((x) => <span key={x} className="rounded-full bg-brand-light px-2.5 py-1 text-xs font-semibold text-brand">{x}</span>)}</div></div>}

            <div><p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Network</p>
              <div className="grid grid-cols-4 gap-2.5" data-testid="admin-modal-stats">
                {[["Followers", s?.followers], ["Following", s?.following], ["Connections", s?.connections], ["Posts", s?.posts]].map(([l, v]) => (
                  <div key={l} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-center"><p className="font-display font-black text-xl text-slate-900">{v ?? 0}</p><p className="text-[10px] font-bold uppercase text-slate-400">{l}</p></div>
                ))}
              </div>
            </div>

            <div className="flex justify-end pt-2 border-t border-slate-100">
              <Button data-testid="admin-modal-suspend" onClick={toggleSuspend} variant="outline" className={`gap-1.5 font-semibold ${b.suspended ? "border-emerald-300 text-emerald-700" : "border-red-300 text-red-600"}`}><Ban size={15} /> {b.suspended ? "Reinstate" : "Suspend"}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SeedBusinesses() {
  const blank = { business_name: "", trade_name: "", category: "", city: "", state: "", gst_number: "", description: "" };
  const [form, setForm] = useState(blank);
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(false);
  const [seeded, setSeeded] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [report, setReport] = useState(null);
  const fileRef = useRef(null);
  useEffect(() => { api.get("/categories").then(({ data }) => setCats(data.categories)).catch(() => {}); }, []);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const slugPreview = [form.business_name, form.city].filter(Boolean).join(" ").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "business-name";

  const save = async () => {
    if (!form.business_name.trim()) { toast.error("Business name is required"); return; }
    if (!form.category) { toast.error("Select a category"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/businesses/seed", form);
      toast.success(`Seeded "${data.business_name}" as ${data.gst_verified ? "GST Verified" : "Basic"} · unclaimed`);
      setSeeded((s) => [data, ...s]);
      setForm((f) => ({ ...blank, category: f.category, city: f.city, state: f.state }));
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/businesses/bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReport(data);
      if (data.imported > 0) toast.success(`Successfully imported ${data.imported} businesses`);
      else toast.error("No businesses imported — check the errors below");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="seed-form">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h2 className="font-display font-bold text-lg text-slate-900">Bulk Seed Business</h2>
          <div>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm" className="hidden" data-testid="bulk-upload-input" onChange={(e) => { uploadFile(e.target.files?.[0]); e.target.value = ""; }} />
            <Button type="button" data-testid="bulk-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading} variant="outline" size="sm" className="gap-1.5 border-brand text-brand font-semibold hover:bg-brand hover:text-white">
              {uploading ? <><Loader2 className="animate-spin" size={15} /> Importing…</> : <><Upload size={15} /> Upload Spreadsheet</>}
            </Button>
          </div>
        </div>
        <p className="text-sm text-slate-500 mb-5">Fast-entry curated profiles. Saved as <span className="font-semibold text-slate-700">unclaimed</span> so owners can claim them later. Valid GSTIN → GST Verified (blue tick); no GSTIN → Basic. CSV/XLSX columns: Business Name, Trade Name, Category, City, State, GSTIN (optional), SEO Description.</p>

        {report && (
          <div data-testid="bulk-upload-report" className="mb-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="font-bold text-slate-900">Successfully imported {report.imported} business{report.imported === 1 ? "" : "es"}</p>
            <p data-testid="bulk-upload-tier-counts" className="text-sm font-semibold text-slate-700 mt-0.5"><span className="text-[#166534]">{report.gst_imported ?? 0} GST Verified imported</span>, <span className="text-slate-600">{report.basic_imported ?? 0} Basic imported</span>, <span className={report.error_count ? "text-red-600" : "text-slate-500"}>{report.error_count} error{report.error_count === 1 ? "" : "s"}</span>.</p>
            <p className="text-sm text-slate-500">{report.total_rows} rows processed{report.auto_mapped_count > 0 && ` · ${report.auto_mapped_count} auto-mapped`}{report.review_count > 0 && ` · ${report.review_count} need review`}</p>
            <CategoryMappingReport report={report} />
            {report.errors?.length > 0 && (
              <div className="mt-3 max-h-48 overflow-y-auto rounded-lg border border-red-100 bg-white" data-testid="bulk-upload-errors">
                {report.errors.map((er, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-1.5 text-xs border-b border-slate-100 last:border-0">
                    <span className="font-mono text-slate-400 shrink-0">Row {er.row}</span>
                    <span className="font-semibold text-slate-700 truncate">{er.name}</span>
                    <span className="text-red-600 ml-auto shrink-0">{er.error}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Business Name *</label>
            <Input data-testid="seed-business-name" value={form.business_name} onChange={(e) => set("business_name")(e.target.value)} placeholder="e.g. Sunrise Textiles" className="h-11 bg-slate-50" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Trade Name</label>
            <Input data-testid="seed-trade-name" value={form.trade_name} onChange={(e) => set("trade_name")(e.target.value)} placeholder="Optional" className="h-11 bg-slate-50" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Category *</label>
            <select data-testid="seed-category" value={form.category} onChange={(e) => set("category")(e.target.value)} className="w-full h-11 rounded-md border border-slate-200 bg-slate-50 px-3 font-semibold text-slate-800 focus:border-brand focus:outline-none">
              <option value="">Select category…</option>
              {cats.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">City</label>
            <Input data-testid="seed-city" value={form.city} onChange={(e) => set("city")(e.target.value)} placeholder="e.g. Surat" className="h-11 bg-slate-50" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">State</label>
            <Input data-testid="seed-state" value={form.state} onChange={(e) => set("state")(e.target.value)} placeholder="e.g. Gujarat" className="h-11 bg-slate-50" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">GSTIN <span className="normal-case font-medium text-slate-400">(optional, leave blank for Basic)</span></label>
            <Input data-testid="seed-gstin" value={form.gst_number} onChange={(e) => set("gst_number")(e.target.value.toUpperCase())} placeholder="27AAPFU0939F1ZV" className="h-11 bg-slate-50 font-mono tracking-wide" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">SEO Business Description</label>
            <textarea data-testid="seed-description" value={form.description} onChange={(e) => set("description")(e.target.value)} rows={6} placeholder="Rich, keyword-friendly description of the business, its products and specialities…" className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-slate-800 focus:border-brand focus:outline-none resize-y" />
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2 text-sm text-slate-500">
          <span className="font-semibold text-slate-600">SEO URL:</span>
          <code data-testid="seed-slug-preview" className="rounded bg-slate-100 px-2 py-1 text-brand font-mono text-xs">/business/{slugPreview}</code>
        </div>

        <Button data-testid="seed-save-btn" onClick={save} disabled={saving} className="mt-5 w-full bg-brand hover:bg-brand-hover font-semibold h-11">
          {saving ? <Loader2 size={16} className="animate-spin" /> : "Save Unclaimed Business"}
        </Button>
      </div>

      <div>
        <h2 className="font-display font-bold text-lg text-slate-900 mb-4">Seeded This Session ({seeded.length})</h2>
        {seeded.length === 0 ? (
          <p className="text-slate-500 text-sm">Saved businesses will appear here for quick access.</p>
        ) : (
          <div data-testid="seed-recent-list" className="space-y-3">
            {seeded.map((b) => (
              <div key={b.id} data-testid={`seed-item-${b.id}`} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3">
                <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><Building2 size={18} className="text-slate-400" /></div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-900 truncate flex items-center gap-1.5">{b.business_name}{b.gst_verified && <ShieldCheck size={13} className="text-[#22C55E]" />}</p>
                  <p className="text-xs text-slate-500 truncate">{b.category} · <span data-testid={`seed-tier-${b.id}`} className="font-semibold text-slate-600">{b.gst_verified ? "GST Verified" : "Basic"}</span> · <span className="text-amber-600 font-semibold">unclaimed</span></p>
                </div>
                <a href={`/business/${b.slug}`} target="_blank" rel="noopener noreferrer" data-testid={`seed-view-${b.id}`} className="p-2 rounded-md text-slate-500 hover:bg-slate-100"><ExternalLink size={16} /></a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AdminNews() {
  const empty = { title: "", category: NEWS_CATEGORIES[0], cover_url: "", content: "", published: true };
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/news"); setItems(data); }
    catch (e) { console.error("Failed to load news", e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const reset = () => { setForm(empty); setEditingId(null); };

  const uploadCover = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, cover_url: data.logo_url }));
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) { toast.error("Title and content are required"); return; }
    setSaving(true);
    try {
      if (editingId) { await api.put(`/admin/news/${editingId}`, form); toast.success("Article updated"); }
      else { await api.post("/admin/news", form); toast.success("Article published"); }
      reset();
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const edit = (a) => {
    setEditingId(a.id);
    setForm({ title: a.title, category: a.category, cover_url: a.cover_url || "", content: a.content || "", published: a.published });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const togglePublish = async (a) => {
    try { await api.put(`/admin/news/${a.id}`, { title: a.title, category: a.category, cover_url: a.cover_url, content: a.content, published: !a.published }); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try { await api.delete(`/admin/news/${id}`); setItems((x) => x.filter((a) => a.id !== id)); toast.success("Deleted"); if (editingId === id) reset(); }
    catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Editor */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-news-editor">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-lg text-slate-900">{editingId ? "Edit Article" : "New Article"}</h2>
          {editingId && (
            <button data-testid="news-cancel-edit" onClick={reset} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800">
              <X size={15} /> Cancel
            </button>
          )}
        </div>

        <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Title</label>
        <Input data-testid="news-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. GST update for manufacturers" className="mb-4" />

        <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Category</label>
        <select data-testid="news-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full mb-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 font-semibold text-slate-800 focus:border-brand focus:outline-none">
          {NEWS_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>

        <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Cover Image</label>
        <div className="mb-4 flex items-center gap-3">
          {form.cover_url ? (
            <img src={mediaUrl(form.cover_url)} alt="cover" className="h-16 w-24 rounded-lg object-cover border border-slate-200" />
          ) : (
            <div className="h-16 w-24 rounded-lg bg-slate-100 flex items-center justify-center"><ImageIcon size={20} className="text-slate-400" /></div>
          )}
          <label className="cursor-pointer inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:border-brand">
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <ImageIcon size={15} />} {form.cover_url ? "Replace" : "Upload"}
            <input data-testid="news-cover-input" type="file" accept="image/*" className="hidden" onChange={(e) => uploadCover(e.target.files?.[0])} />
          </label>
          {form.cover_url && <button onClick={() => setForm({ ...form, cover_url: "" })} className="text-sm text-slate-400 hover:text-red-500">Remove</button>}
        </div>

        <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Content</label>
        <textarea data-testid="news-content-input" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={10} placeholder="Write your article… Use blank lines to separate paragraphs." className="w-full mb-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-slate-800 focus:border-brand focus:outline-none resize-y" />

        <label className="flex items-center gap-2 mb-5 cursor-pointer select-none">
          <input data-testid="news-published-toggle" type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="h-4 w-4 accent-[#0F3D8C]" />
          <span className="text-sm font-semibold text-slate-700">Published (visible on the public News page)</span>
        </label>

        <Button data-testid="news-save-btn" onClick={save} disabled={saving} className="w-full bg-brand hover:bg-brand-hover font-semibold">
          {saving ? <Loader2 size={16} className="animate-spin" /> : editingId ? "Update Article" : "Publish Article"}
        </Button>
      </div>

      {/* List */}
      <div>
        <h2 className="font-display font-bold text-lg text-slate-900 mb-4">All Articles ({items.length})</h2>
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-brand" size={28} /></div>
        ) : items.length === 0 ? (
          <p className="text-slate-500 text-sm">No articles yet. Publish your first one.</p>
        ) : (
          <div data-testid="admin-news-list" className="space-y-3">
            {items.map((a) => (
              <div key={a.id} data-testid={`admin-news-item-${a.id}`} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3">
                {a.cover_url ? <img src={mediaUrl(a.cover_url)} alt="" className="h-14 w-20 rounded-lg object-cover shrink-0" /> : <div className="h-14 w-20 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><Newspaper size={16} className="text-slate-400" /></div>}
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-900 truncate">{a.title}</p>
                  <p className="text-xs text-slate-500">{a.category} · {a.published ? <span className="text-green-600 font-semibold">Published</span> : <span className="text-amber-600 font-semibold">Hidden</span>}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button data-testid={`news-toggle-${a.id}`} onClick={() => togglePublish(a)} title={a.published ? "Hide" : "Publish"} className="p-2 rounded-md text-slate-500 hover:bg-slate-100">{a.published ? <Eye size={16} /> : <EyeOff size={16} />}</button>
                  <button data-testid={`news-edit-${a.id}`} onClick={() => edit(a)} className="p-2 rounded-md text-slate-500 hover:bg-slate-100"><Pencil size={16} /></button>
                  <button data-testid={`news-delete-${a.id}`} onClick={() => remove(a.id)} className="p-2 rounded-md text-red-500 hover:bg-red-50"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AdminMedia() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => { setLoading(true); try { const { data } = await api.get("/admin/media"); setItems(data); } catch (e) { console.error("Failed to load media", e); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);
  const remove = async (id) => { try { await api.delete(`/media/${id}`); setItems((x) => x.filter((m) => m.id !== id)); toast.success("Media deleted"); } catch (e) { toast.error("Delete failed"); } };
  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-brand" size={28} /></div>;
  if (items.length === 0) return <div data-testid="admin-media-empty" className="py-16 text-center text-slate-500">No business media uploaded yet.</div>;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="admin-media-grid">
      {items.map((m) => (
        <div key={m.id} data-testid={`admin-media-${m.id}`} className="relative aspect-square rounded-xl overflow-hidden bg-slate-100 group border border-slate-200">
          {m.media_type === "video" ? (
            <><video src={mediaUrl(m.url)} className="h-full w-full object-cover" /><span className="absolute inset-0 flex items-center justify-center bg-black/25"><Play size={20} className="text-white" /></span></>
          ) : (
            <img src={mediaUrl(m.url)} alt="" className="h-full w-full object-cover" />
          )}
          <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[10px] px-1.5 py-1 truncate">{m.business_name || "—"}</span>
          <button data-testid={`admin-delete-media-${m.id}`} onClick={() => remove(m.id)} className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-black/60 text-white flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity hover:bg-red-600"><Trash2 size={14} /></button>
        </div>
      ))}
    </div>
  );
}

function Reports() {  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => { setLoading(true); try { const { data } = await api.get("/admin/reports"); setItems(data); } catch (e) { console.error("Failed to load reports", e); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);
  const hide = async (postId, val) => { try { await api.post(`/admin/posts/${postId}/hide`, { hidden: val }); toast.success(val ? "Post hidden" : "Post shown"); } catch (e) { toast.error("Failed"); } };
  const resolve = async (id) => { try { await api.post(`/admin/reports/${id}/resolve`); setItems((it) => it.filter((r) => r.id !== id)); } catch (e) { console.error("Failed to resolve report", e); } };
  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-brand" size={28} /></div>;
  if (items.length === 0) return <div data-testid="admin-reports-empty" className="py-16 text-center text-slate-500">No open reports. All clear.</div>;
  return (
    <div className="space-y-3" data-testid="admin-reports-list">{items.map((r) => (
      <div key={r.id} className="border border-slate-200 rounded-xl bg-white p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0"><p className="text-xs font-bold uppercase text-slate-400">{r.target_type} report</p>
            {r.target?.content && <p className="text-slate-700 mt-1 line-clamp-2">"{r.target.content}"</p>}
            <p className="text-xs text-slate-400 mt-1">Reason: {r.reason}</p></div>
          <div className="flex gap-2 shrink-0">
            {r.target_type === "post" && <Button data-testid={`hide-${r.target_id}`} onClick={() => hide(r.target_id, !r.target?.hidden)} variant="outline" className="gap-1.5 border-slate-300">{r.target?.hidden ? <Eye size={15} /> : <EyeOff size={15} />} {r.target?.hidden ? "Show" : "Hide"}</Button>}
            <Button data-testid={`resolve-${r.id}`} onClick={() => resolve(r.id)} variant="outline" className="border-emerald-300 text-emerald-700 font-semibold">Resolve</Button>
          </div>
        </div>
      </div>))}</div>
  );
}
